// Excel + PDF report builders. Mirror the structure of the original
// "B1S 2026 CRM Pipeline.xlsx" so output is familiar.

import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { STAGES, DEFAULT_TARGETS } from './stages';
import { summarize, weeklyKpis, isoWeekKey, startOfWeek, endOfWeek } from './metrics';

function fmtDate(d) {
  if (!d) return '';
  const dd = d.toDate ? d.toDate() : new Date(d);
  return isNaN(dd) ? '' : dd.toISOString().slice(0,10);
}

/** Build & download an .xlsx weekly report. */
export function buildExcelReport(leads, { weekDate = new Date(), filename, targets = DEFAULT_TARGETS } = {}) {
  const wb = XLSX.utils.book_new();
  const sum = summarize(leads);
  const weeks = weeklyKpis(leads, { weeks: 12, endDate: weekDate });
  const wkStart = startOfWeek(weekDate);
  const wkEnd   = endOfWeek(weekDate);

  // Sheet 1: Pipeline (full lead dump, matching the source schema)
  const pipelineRows = leads.map((l, i) => ({
    'Lead ID': l.legacyId || (i + 1),
    'Company Name': l.companyName,
    'Industry/Vertical': l.industry,
    'Contact Name': l.contactName,
    'Phone': l.phone,
    'Email': l.email,
    'Pipeline Stage': l.stage,
    'Product Interest': l.productInterest,
    'Lead Source': l.leadSource,
    'Date Added': fmtDate(l.dateAdded),
    'Last Activity Date': fmtDate(l.lastActivityAt),
    'Next Action': l.nextAction,
    'Next Action Date': fmtDate(l.nextActionDate),
    'Owner': l.ownerName,
    'Notes': l.notes,
    'Estimated Monthly Value (KES)': Number(l.estimatedMonthlyValueKES || 0)
  }));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(pipelineRows), 'Pipeline');

  // Sheet 2: KPI Dashboard (12-week scorecard with Target/Total/Average/% rows)
  const kpiHeader = ['Week', 'New Prospects Enriched', 'Outreach Emails Sent', 'Discovery Calls Completed',
                     'Qualified Leads (Stage 4)', 'Founder Presentations', 'Proposals Sent', 'Retainers Closed'];
  const kpiRows = [
    ['2026 Sales KPI Dashboard - 12 Week Tracking'],
    kpiHeader,
    ['Target',
      targets.newProspectsEnriched, targets.outreachEmailsSent, targets.discoveryCallsCompleted,
      targets.qualifiedLeads, targets.founderPresentations, targets.proposalsSent, targets.retainersClosed
    ],
    ...weeks.map(w => [
      w.key,
      w.newProspectsEnriched, w.outreachEmailsSent, w.discoveryCallsCompleted,
      w.qualifiedLeads, w.founderPresentations, w.proposalsSent, w.retainersClosed
    ]),
    ['TOTAL',
      sumCol(weeks, 'newProspectsEnriched'), sumCol(weeks, 'outreachEmailsSent'), sumCol(weeks, 'discoveryCallsCompleted'),
      sumCol(weeks, 'qualifiedLeads'), sumCol(weeks, 'founderPresentations'), sumCol(weeks, 'proposalsSent'), sumCol(weeks, 'retainersClosed')
    ]
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(kpiRows), 'KPI Dashboard');

  // Sheet 3: Channel Attribution
  const ch = leads.map((l, i) => ({
    'Lead ID': l.legacyId || (i + 1),
    'Company Name': l.companyName,
    'Lead Source Channel': l.leadSource,
    'Date Entered Pipeline': fmtDate(l.dateAdded),
    'Current Stage': l.stage,
    'Revenue (KES)': l.stage === 'Closed Won' ? Number(l.estimatedMonthlyValueKES || 0) : 0,
    'Month': fmtDate(l.dateAdded).slice(0, 7)
  }));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ch), 'Channel Attribution');

  // Sheet 4: Weekly Summary (this week)
  const inWeek = (d) => {
    const dd = d?.toDate ? d.toDate() : (d ? new Date(d) : null);
    return dd && dd >= wkStart && dd < wkEnd;
  };
  const newThisWeek = leads.filter(l => inWeek(l.dateAdded));
  const movedThisWeek = leads.filter(l => inWeek(l.lastActivityAt));
  const wkSheet = [
    [`B1S Weekly Report — ${isoWeekKey(wkStart)}`],
    [`${fmtDate(wkStart)} → ${fmtDate(new Date(wkEnd.getTime() - 86400000))}`],
    [],
    ['Headline KPIs'],
    ['Open leads', sum.openLeads],
    ['Total pipeline value (KES)', sum.totalPipelineKES],
    ['Won this period (count)', sum.wonCount],
    ['Won (KES, MRR)', sum.wonKES],
    ['Win rate', `${Math.round(sum.winRate * 100)}%`],
    [],
    ['New leads added this week', newThisWeek.length],
    ['Leads with activity this week', movedThisWeek.length],
    [],
    ['Stage breakdown'],
    ['Stage', 'Count', 'Value (KES)'],
    ...sum.byStage.map(s => [s.stage, s.count, s.valueKES]),
    [],
    ['Top 10 deals by value'],
    ['Company', 'Stage', 'Owner', 'KES/mo'],
    ...[...leads].sort((a,b) => Number(b.estimatedMonthlyValueKES||0) - Number(a.estimatedMonthlyValueKES||0))
                .slice(0, 10)
                .map(l => [l.companyName, l.stage, l.ownerName, Number(l.estimatedMonthlyValueKES||0)])
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(wkSheet), 'Weekly Summary');

  XLSX.writeFile(wb, filename || `B1S_Weekly_Report_${isoWeekKey(wkStart)}.xlsx`);
}

/** Build & download a PDF weekly report. */
export function buildPdfReport(leads, { weekDate = new Date(), filename } = {}) {
  const sum = summarize(leads);
  const weeks = weeklyKpis(leads, { weeks: 12, endDate: weekDate });
  const wkStart = startOfWeek(weekDate);
  const wkEnd = endOfWeek(weekDate);
  const wk = isoWeekKey(wkStart);

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const w = doc.internal.pageSize.getWidth();

  // Header — Binary One brand: black band with green accent line
  doc.setFillColor(15, 15, 15);                    // ink-900
  doc.rect(0, 0, w, 70, 'F');
  doc.setFillColor(0, 166, 81);                    // brand-500 green
  doc.rect(0, 70, w, 4, 'F');
  doc.setTextColor(255);
  doc.setFontSize(20).setFont('helvetica', 'bold');
  doc.text('B1S CRM — Weekly Report', 40, 35);
  // Tiny logo bars on the right side of the header
  doc.setFillColor(0, 166, 81);
  doc.rect(w - 70, 18, 30, 6, 'F');
  doc.rect(w - 70, 30, 30, 6, 'F');
  doc.rect(w - 70, 42, 30, 6, 'F');
  doc.setFontSize(11).setFont('helvetica', 'normal');
  doc.text(`${wk}   |   ${fmtDate(wkStart)} → ${fmtDate(new Date(wkEnd.getTime() - 86400000))}`, 40, 55);
  doc.setTextColor(0);

  // Headline KPIs (mini grid)
  let y = 100;
  doc.setFontSize(13).setFont('helvetica', 'bold').text('Headline KPIs', 40, y);
  y += 14;
  autoTable(doc, {
    startY: y,
    head: [['Open leads', 'Pipeline (KES/mo)', 'Won (count)', 'Won (KES/mo)', 'Win rate']],
    body: [[
      sum.openLeads,
      Math.round(sum.totalPipelineKES).toLocaleString(),
      sum.wonCount,
      Math.round(sum.wonKES).toLocaleString(),
      `${Math.round(sum.winRate*100)}%`
    ]],
    theme: 'grid', headStyles: { fillColor: [0, 166, 81] }, styles: { fontSize: 10 }
  });

  // Stage funnel
  doc.setFontSize(13).setFont('helvetica', 'bold').text('Stage funnel', 40, doc.lastAutoTable.finalY + 28);
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 36,
    head: [['Stage', 'Count', 'Pipeline value (KES)']],
    body: sum.byStage.map(s => [s.stage, s.count, Math.round(s.valueKES).toLocaleString()]),
    theme: 'striped', headStyles: { fillColor: [0, 166, 81] }, styles: { fontSize: 10 }
  });

  // 12-week KPI table
  doc.setFontSize(13).setFont('helvetica', 'bold').text('12-Week KPI scorecard', 40, doc.lastAutoTable.finalY + 28);
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 36,
    head: [['Week', 'New', 'Qualified', 'Presentations', 'Proposals', 'Retainers Won']],
    body: weeks.map(w => [w.key, w.newProspectsEnriched, w.qualifiedLeads, w.founderPresentations, w.proposalsSent, w.retainersClosed]),
    theme: 'striped', headStyles: { fillColor: [0, 166, 81] }, styles: { fontSize: 9 }
  });

  // Top 10 deals — start a new page if running low
  if (doc.lastAutoTable.finalY > 680) doc.addPage();
  const topY = doc.lastAutoTable.finalY > 680 ? 60 : doc.lastAutoTable.finalY + 28;
  doc.setFontSize(13).setFont('helvetica', 'bold').text('Top 10 deals by value', 40, topY);
  autoTable(doc, {
    startY: topY + 8,
    head: [['Company', 'Stage', 'Owner', 'KES/mo']],
    body: [...leads]
      .sort((a,b) => Number(b.estimatedMonthlyValueKES||0) - Number(a.estimatedMonthlyValueKES||0))
      .slice(0, 10)
      .map(l => [l.companyName, l.stage, l.ownerName || '', Math.round(Number(l.estimatedMonthlyValueKES||0)).toLocaleString()]),
    theme: 'grid', headStyles: { fillColor: [0, 166, 81] }, styles: { fontSize: 10 }
  });

  // Footer (page numbers)
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(8).setTextColor(120);
    doc.text(`Generated ${new Date().toLocaleString()}  ·  Page ${i}/${pages}`, 40, doc.internal.pageSize.getHeight() - 20);
  }

  doc.save(filename || `B1S_Weekly_Report_${wk}.pdf`);
}

function sumCol(rows, k) { return rows.reduce((s, r) => s + (r[k] || 0), 0); }
